import { Link, useOutletContext } from "react-router";
import { usePendingCount, usePosts } from "../hooks/usePosts";
import { PostCard } from "../components/cards/PostCard";

export function Dashboard() {
  const { date } = useOutletContext<{ date: string }>();
  const { data: posts = [], isLoading, error } = usePosts(date);
  const { data: pendingCount = 0 } = usePendingCount();

  const reels = posts.filter((p) => p.is_video);
  const carousels = posts.filter((p) => !p.is_video && p.slide_count > 1);
  const singles = posts.filter((p) => !p.is_video && p.slide_count <= 1);

  if (isLoading) {
    return <div className="text-brand-text-dark/60">Lade Posts…</div>;
  }
  if (error) {
    return (
      <div className="rounded-xl bg-red-100 p-6 text-red-900">
        Fehler beim Laden: {String(error)}
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="headline text-6xl text-brand-text-dark">Dashboard</h1>
          <p className="mt-2 text-sm text-brand-text-dark/60">
            {posts.length} Posts · {reels.length} Reels · {carousels.length} Carousels · {singles.length} Einzelposts
          </p>
        </div>
      </div>

      {pendingCount > 0 ? (
        <Link
          to="/review"
          className="flex items-center gap-4 rounded-2xl bg-brand-magenta/10 px-5 py-4 text-sm ring-1 ring-brand-magenta/30 transition hover:bg-brand-magenta/15"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-magenta text-lg font-bold text-white tabular-nums">
            {pendingCount}
          </span>
          <div className="flex-1">
            <div className="font-bold text-brand-text-dark">
              {pendingCount === 1
                ? "1 Post wartet auf Review"
                : `${pendingCount} Posts warten auf Review`}
            </div>
            <div className="text-xs text-brand-text-dark/60">
              Freigeben oder ablehnen — das Feedback fließt in die nächste Generation ein.
            </div>
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-brand-magenta">
            Öffnen →
          </span>
        </Link>
      ) : null}

      <Section title="Reels" count={reels.length}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {reels.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      </Section>

      <Section title="Carousels" count={carousels.length}>
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          {carousels.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      </Section>

      <Section title="Einzelposts" count={singles.length}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {singles.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      </Section>

      {/* Empty state — only when there are 0 posts on the selected date */}
      {posts.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-brand-text-dark/15 bg-white/40 p-12 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue/10 text-2xl">
            ✨
          </div>
          <h2 className="text-lg font-bold text-brand-text-dark">
            Heute noch keine Posts
          </h2>
          <p className="mt-1 text-sm text-brand-text-dark/60">
            Wähl ein anderes Datum oben oder
            <Link
              to="/generate"
              className="ml-1 font-bold text-brand-blue hover:underline"
            >
              starte einen neuen Lauf
            </Link>
            .
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <section>
      <h2 className="mb-4 flex items-baseline gap-3 text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/50">
        {title}
        <span className="text-[10px] font-normal opacity-60">({count})</span>
      </h2>
      {children}
    </section>
  );
}
